import { Button, Calendar, Icon, Skeleton } from 'antd';
import _ from 'lodash';
import moment, { max } from 'moment';
import React, { useEffect, useState, useRef } from 'react';
import queryString from 'query-string';
import './AvailabilityCalendar.scss';
import config from '../../Config/config';
import { CalendarRepository } from '../../Repositories/CalendarRepository';
import getChunkedSlots from '../../Utility/getChunkedSlots';
import chunkSlots from '../../Utility/chunkSlots';
import { getCalendarUsername } from '../../Utility/inviteLink';
import openNotification from '../../Utility/notification';
import { toInstant } from '../../Utility/proposeLink';

const calendarRepository = new CalendarRepository();

const AvailabilityCalendar = props => {
  const {
    fetchFreeSlots,
    settings,
    freeSlots,
    updateEventById,
    setShowCalendar,
    event: prevEvent,
    dataSource,
    expiryStartTime,
    expiryEndTime,
    disableFetchSlots,
    freeSlotsApiStatus,
  } = props;
  const expiryStart = toInstant(expiryStartTime);
  const expiryEnd = toInstant(expiryEndTime);

  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedTime, setSelectedTime] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDateSelected, setIsDateSelected] = useState(false);
  const dateSlotRef = useRef();
  const { eventId } = queryString.parse(window.location.search);
  const username = getCalendarUsername();
  const getInterval = localDate => {
    const today = moment();
    const monthStart = localDate.clone().startOf('month');
    const monthEnd = localDate.clone().endOf('month');
    const startDate = moment.max(today, monthStart);
    const endDate = moment.max(today, monthEnd);

    return {
      start_date: startDate
        .clone()
        .utc()
        .toISOString(),
      end_date: endDate
        .clone()
        .utc()
        .toISOString(),
    };
  };
  const retryFreeSlots = () => fetchFreeSlots(getInterval(currentDate), prevEvent.EventType, prevEvent.UserIds);
  const monthHasSlots = Object.keys(freeSlots || {}).some(day => day.startsWith(currentDate.format('YYYY-MM')) && _.get(freeSlots, day, []).length);

  useEffect(() => {
    if (!settings) return;
    let startDay = currentDate;
    if (expiryStart) {
      startDay = max(moment(expiryStart), startDay);
    }
    if (!disableFetchSlots) fetchFreeSlots(getInterval(startDay), prevEvent.EventType, prevEvent.UserIds);
  }, [settings]);

  useEffect(() => {
    if (dataSource !== 'conversation') return;
    if (!expiryStart) return;
    setCurrentDate(moment.max(moment(), moment(expiryStart)).clone());
  }, [dataSource, expiryStartTime]);

  useEffect(() => {
    if (currentDate) {
      // eslint-disable-next-line no-use-before-define
      onDateSelect(currentDate);
    }
  }, [JSON.stringify(_.get(freeSlots, currentDate.format('YYYY-MM-DD'), []))]);

  const handleMonthChange = newValue => {
    if (!disableFetchSlots) fetchFreeSlots(getInterval(newValue), prevEvent.EventType, prevEvent.UserIds);
    setCurrentDate(newValue);
    setSelectedTime(null);
    setIsDateSelected(false);
  };

  const getAllSlots = date => {
    const startOfDay = moment(date).startOf('day');
    // Exclusive, not `endOf('day')`: that is 23:59:59.999, so the final chunk of a
    // window running to midnight ends at 00:00:00.000, does not fit, and is dropped.
    const endOfDay = moment(date)
      .startOf('day')
      .add(1, 'day');
    return getChunkedSlots(startOfDay, endOfDay, settings);
  };

  const getDisabledStatus = date => {
    const today = moment();
    let startDay = today;
    if (expiryStart) {
      startDay = max(moment(expiryStart), startDay);
    }
    if (date.isBefore(startDay.clone().startOf('day'))) return true;
    if (expiryEnd) {
      const endDay = max(moment(expiryEnd), today);
      const isAfter = date.isAfter(endDay.clone().endOf('day'));
      if (isAfter) return true;
    }
    if (date.month() !== currentDate.month()) return true;
    if (disableFetchSlots) {
      const slots = getAllSlots(date);
      return !slots.length;
    }
    return !_.get(freeSlots, date.format('YYYY-MM-DD'), []).length;
  };

  const onDateSelect = date => {
    if (getDisabledStatus(date)) return;
    let slots = [];
    if (disableFetchSlots) {
      slots = getAllSlots(date);
    } else {
      const currentDayFreeSlots = _.get(freeSlots, date.format('YYYY-MM-DD'), []);
      const slotDuration = _.get(settings, 'SlotDuration', 30);
      const now = moment();
      currentDayFreeSlots.forEach(({ slotStart, slotEnd }) => {
        if (slotStart.format('HH:mm') === slotEnd.format('HH:mm')) {
          return;
        }
        slots.push(...chunkSlots(slotStart, slotEnd, slotDuration, now));
      });
    }
    if (date.month() !== currentDate.month()) {
      setIsDateSelected(false);
    } else {
      setIsDateSelected(true);
    }
    setTimeout(() => {
      if (dateSlotRef.current) {
        dateSlotRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }, 200);
    setCurrentDate(date);
    setSelectedTime(null);
    setTimeSlots(slots);
  };

  const renderHeader = ({ value: currentValue, onChange }) => {
    const handlePrevMonth = () => {
      const newValue = currentValue.clone().subtract(1, 'month');
      handleMonthChange(newValue, onChange);
      onChange(newValue);
    };
    const handleNextMonth = () => {
      const newValue = currentValue.clone().add(1, 'month');
      handleMonthChange(newValue, onChange);
      onChange(newValue);
    };

    return (
      <div className="date-picker-header">
        <button className="navigation-arrow" onClick={handlePrevMonth} type="button">
          <Icon type="left" />
        </button>
        <div className="current-month">{currentValue.format('MMMM YYYY')}</div>
        <button className="navigation-arrow" onClick={handleNextMonth} type="button">
          <Icon type="right" />
        </button>
      </div>
    );
  };

  const dateCellRender = date => {
    const currentMonth = currentDate ? currentDate.month() : moment().month();
    const isCurrentMonth = date.month() === currentMonth;
    const isDisabled = getDisabledStatus(date);
    const isSelectedDate = isDateSelected && currentDate.isSame(date, 'day');
    const cellClassName = `date-cell ${isDateSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${
      isCurrentMonth ? 'current-month' : ''
    } ${isSelectedDate ? 'current-day' : ''}`;
    return (
      <div className={cellClassName}>
        <div className="date-number">{date.date()}</div>
        {isSelectedDate && <div className="selected-dot" />}
      </div>
    );
  };

  const isDisabledDate = date => {
    const today = moment();
    let startDay = today;

    if (expiryStart) {
      startDay = max(moment(expiryStart), startDay);
    }
    if (expiryEnd) {
      const endDay = max(moment(expiryEnd), today);
      const isAfter = date.isAfter(endDay.clone().endOf('day'));
      if (isAfter) return true;
    }
    if (disableFetchSlots) return false;
    const isBeforeNow = date.isBefore(startDay.clone().startOf('day'));
    return isBeforeNow;
  };

  const onDateTimeSubmit = async time => {
    setIsSubmitting(true);
    const [hours, minutes] = time.split(':').map(Number);
    const localTime = moment().set({
      year: currentDate.year(),
      month: currentDate.month(),
      date: currentDate.date(),
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    });
    const startTime = moment.utc(localTime);
    const slotDuration = _.get(settings, 'SlotDuration', 30);
    const endTime = startTime.clone().add(slotDuration, 'minutes');

    if (dataSource === 'conversation') {
      let description = prevEvent.Description;
      if (!prevEvent.Location) {
        const pattern = /<b>Contact Number:<\/b>.*?<br\s*\/?>/i;
        description = description.replace(pattern, '');
      } else {
        description = description.replace('{PHONE_NUMBER}', prevEvent.Location);
      }
      const event = {
        StartTime: startTime.toISOString(),
        EndTime: endTime.toISOString(),
        Attendees: prevEvent.Attendees,
        Initiator: username,
        Location: prevEvent.Location,
        Description: description,
        Summary: prevEvent.Summary,
        ReferenceId: prevEvent.ReferenceId,
        EventId: prevEvent.ReferenceId,
        EventType: prevEvent.EventType,
        UserIds: prevEvent.UserIds,
      };
      try {
        await calendarRepository.createEvent(event);
        setShowCalendar(false);
        setIsSubmitting(false);
      } catch (error) {
        if (error?.response?.data?.Type === 'FREE_SLOT_NOT_FOUND') {
          openNotification(
            'topLeft',
            'Failed to save',
            'The selected time slot is no longer available. Please choose another from the updated list',
            'warning'
          );
          await fetchFreeSlots(getInterval(currentDate), prevEvent.EventType, prevEvent.UserIds);
        } else {
          openNotification('topLeft', 'Failed to save', 'Error occured while creating Event');
        }
        setIsSubmitting(false);
      }
      return;
    }

    const event = {
      StartTime: startTime.toISOString(),
      EndTime: endTime.toISOString(),
      AttendeeStatus: 'Proposed',
      Attendee: username,
      EventId: eventId,
      RefererHost: config.refererHost,
      EventMethod: null,
      IsByCandidate: true,
    };
    updateEventById(eventId, event)
      .then(() => {
        setShowCalendar(false);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const renderTimeSlot = time => {
    const formattedTime = moment(time, 'HH:mm').format('hh:mm A');

    if (time === selectedTime) {
      return (
        <div className="selected-time-slot-container" key={time}>
          <div className="selected-time-slot">{formattedTime}</div>
          <Button
            type="primary"
            className="submit-button"
            onClick={() => onDateTimeSubmit(time)}
            loading={isSubmitting}
          >
            Submit
          </Button>
        </div>
      );
    }
    return (
      <button key={time} type="button" className="time-slot" onClick={() => setSelectedTime(time)}>
        {formattedTime}
      </button>
    );
  };

  return (
    <div className="availability-calendar">
      <div className="availability-calendar-header">Select date and time</div>
      {freeSlotsApiStatus === 'FAILED' && <div role="alert">We couldn&apos;t load availability. <Button onClick={retryFreeSlots}>Retry</Button> Reply to the invitation you received to contact the recruiter.</div>}
      {!disableFetchSlots && freeSlotsApiStatus === 'COMPLETED' && !monthHasSlots && <div>No available times in this range</div>}
      <div className="availability-calendar-body">
        <div
          className="custom-date-picker"
          style={{
            transition: 'all 0.3s ease',
          }}
        >
          <Calendar
            headerRender={renderHeader}
            dateFullCellRender={dateCellRender}
            fullscreen={false}
            value={currentDate || moment()}
            onSelect={onDateSelect}
            disabledDate={date => isDisabledDate(date)}
            onPanelChange={handleMonthChange}
          />
        </div>
        <div className="time-picker" ref={dateSlotRef}>
          {isDateSelected && (
            <>
              <div className="selected-date">{currentDate.format('dddd, MMMM D')}</div>
              <div className="time-slots">
                <Skeleton active paragraph={{ rows: 4 }} loading={freeSlotsApiStatus === 'INPROGRESS'}>
                  {timeSlots.map(time => renderTimeSlot(time))}
                </Skeleton>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
